import { erp } from "@/lib/erp";
import type { DocFieldSchema } from "@/lib/erp/meta";
import type { ListSettingsMetaConfig, ListSettingsFieldConfig } from "./listSettingsTypes";

/**
 * Build available fields and default visible order from Frappe DocType meta
 * 
 * @param config - Meta configuration with filters and rules
 * @param columnIds - Optional list of column IDs for strict mode matching
 * @returns Available fields and default visible order
 */
export async function buildMetaDrivenFields(
  config: ListSettingsMetaConfig,
  columnIds?: string[] // Optional: for strict mode matching
): Promise<{
  availableFields: ListSettingsFieldConfig[];
  defaultVisibleOrder: string[];
}> {
  const {
    doctype,
    includeFieldtypes,
    excludeFieldtypes,
    excludeFieldnames = [],
    respectInListView = true,
    requiredFieldnames = [],
    defaultVisibleFieldnames,
    strictMode = false,
    columnIds: explicitColumnIds,
  } = config;

  // Fetch DocType meta
  const meta = await erp.meta.getDocTypeSchema(doctype);

  // Layout fieldtypes that should never appear
  const layoutFieldtypes = [
    "Section Break",
    "Column Break",
    "HTML",
    "Table",
    "Table MultiSelect",
    "Button",
    "Heading",
  ];

  // Filter safe fields from meta
  const safeFields = meta.fields.filter((f: DocFieldSchema) => {
    // 1. Must have a fieldname
    if (!f.fieldname) return false;

    // 2. Hard hidden list – code-level control 🔐
    if (excludeFieldnames.includes(f.fieldname)) return false;

    // 3. Skip hidden fields
    if (f.hidden === 1) return false;

    // 4. Skip virtual fields (not stored in DB, can't be fetched from list endpoints)
    // Note: is_virtual may not be in DocFieldSchema type, but check if it exists in runtime
    if ((f as any).is_virtual === 1 || (f as any).is_virtual === true) return false;

    // 5. Fieldtype filters
    if (includeFieldtypes && !includeFieldtypes.includes(f.fieldtype)) return false;
    if (excludeFieldtypes && excludeFieldtypes.includes(f.fieldtype)) return false;

    // 6. Ignore layout fields
    if (layoutFieldtypes.includes(f.fieldtype)) return false;

    return true;
  });

  // Strict mode: only include fields that have matching columns
  // BUT: only apply strict filtering if columnIds are explicitly provided and non-empty
  // If strictMode is true but no columnIds are provided, treat as non-strict (show all safe fields)
  let finalFields = safeFields;
  if (strictMode) {
    const allowedIds = explicitColumnIds || columnIds || [];
    // Only filter if we have a non-empty list of column IDs
    if (allowedIds.length > 0) {
      finalFields = safeFields.filter((f) => allowedIds.includes(f.fieldname));
    }
    // If strictMode is true but no columnIds provided, don't filter (show all safe fields)
    // This allows meta-driven behavior while keeping strict mode available for future use
  }

  // Build availableFields (include fieldtype for generic column rendering)
  const availableFields: ListSettingsFieldConfig[] = finalFields.map((f) => ({
    fieldname: f.fieldname,
    label: f.label || f.fieldname,
    required: requiredFieldnames.includes(f.fieldname),
    fromMeta: true,
    fieldtype: f.fieldtype, // Include fieldtype for generic column rendering
  }));

  // Build default visible order
  const defaultVisibleOrder: string[] = [];

  // Priority 1: If defaultVisibleFieldnames is provided (schema override), use it
  if (defaultVisibleFieldnames && defaultVisibleFieldnames.length > 0) {
    // Filter to only include fields that exist in finalFields
    const finalFieldnames = new Set(finalFields.map((f) => f.fieldname));
    defaultVisibleFieldnames.forEach((fname) => {
      if (finalFieldnames.has(fname) && !defaultVisibleOrder.includes(fname)) {
        defaultVisibleOrder.push(fname);
      }
    });
  } else {
    // Priority 2: Start with in_list_view fields (if respectInListView is true)
  if (respectInListView) {
    finalFields
      .filter((f) => f.in_list_view === 1)
      .forEach((f) => {
        if (!defaultVisibleOrder.includes(f.fieldname)) {
          defaultVisibleOrder.push(f.fieldname);
        }
      });
    }
  }

  // Ensure required fields are at the front
  requiredFieldnames.forEach((fname) => {
    if (!defaultVisibleOrder.includes(fname)) {
      defaultVisibleOrder.unshift(fname);
    } else {
      // Move to front if already present
      const index = defaultVisibleOrder.indexOf(fname);
      defaultVisibleOrder.splice(index, 1);
      defaultVisibleOrder.unshift(fname);
    }
  });

  return { availableFields, defaultVisibleOrder };
}

